import React, { useEffect, useState } from 'react';
import './analytics.css';
import PageContainer from 'src/components/container/PageContainer';
import { ToggleButton, ToggleButtonGroup, Button, FormControl, MenuItem, Select } from '@mui/material';
import DashboardCard2 from 'src/components/shared/DashboardCard2';
import { useMain } from 'src/views/hooks/useMain';
import ShowLoader from 'src/components/CustomComponents/ShowLoader';
import { baseUrl, jwtHeaders } from 'src/context/common/data';
import SalesReport from './SalesReport';
import GstBillsPage from 'src/views/Settings/GstBillsData/GstBillsPage';
import FetchSalesExpenseSummary from './fetchSalesExpenseSummary';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { FaDownload } from "react-icons/fa";
import {
  setFilterParams,
  setSalesData,
  setPackageMembershipData,
  setStaffGraphData,
  calculateTotals,
  setLoading,
  setLoadingCurrentYear
} from '../../../redux/slices/salesAnalyticsSlice';

const filterOptions = [
  { label: 'Current Year', value: 'Current Year' },
  { label: 'Current Month', value: 'Current Month' },
  { label: 'Current Week (Sun-Sat)', value: 'Current Week' },
  { label: 'Today', value: 'Today' },
  { label: 'Custom Date', value: 'customDate' },
];

function Analytics() {
  const dispatch = useDispatch();
  const { activeBranchId, fetchBillAnalytics, staffCollection, fetchMemberAnalytixs, fetchPackageAndMembershipBills, branchData } = useMain();

  const [loadingLocal, setLoadingLocal] = useState(true);
  const [packagedata, setPackagedata] = useState(null);
  const [memberdata, setMemberdata] = useState(null);
  const [activeComponent, setActiveComponent] = useState('SalesReport'); // Default to 'SalesReport'
  const [applyTrigger, setApplyTrigger] = useState(0);
  const [summaryData, setSummaryData] = useState(null);

  const {
    filterBy,
    from,
    to,
    tableData,
    loading,
    loadingCurrentYear
  } = useSelector((state) => state.salesAnalytics);

  const handleButtonClick = (component) => {
    setActiveComponent(component);
  };

  const getData2 = async (activeBranchId) => {
    try {
      setLoadingLocal(true);
      const result = await fetch(
        `${baseUrl}/api/billing/packagebilloverview?branchId=${activeBranchId}`,
        {
          method: 'GET',
          headers: jwtHeaders(),
          redirect: 'follow',
        },
      );
      const res = await result.json();
      if (res.statusCode === 200) {
        setPackagedata(res?.data?.packageBills);
        setMemberdata(res?.data?.membershipBills);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingLocal(false);
    }
  };

  const handleToggleChange = (event, newComponent) => {
    if (newComponent) {
      setActiveComponent(newComponent);
    }
  };

  const fetchSalesData = async () => {
    const queryParams = `?filterBy=${filterBy}${from ? `&startDate=${from}` : ''}${to ? `&endDate=${to}` : ''}`;

    try {
      if (filterBy === 'Current Year') {
        dispatch(setLoadingCurrentYear(true));
      } else {
        dispatch(setLoading(true));
      }

      // Fetch all data in parallel
      const [analyticsRes, packageRes, staffRes] = await Promise.all([
        fetchBillAnalytics(activeBranchId, queryParams),
        fetchPackageAndMembershipBills(activeBranchId, `&filterBy=${filterBy}${from ? `&startDate=${from}` : ''}${to ? `&endDate=${to}` : ''}`),
        staffCollection(activeBranchId, queryParams)
      ]);

      if (analyticsRes.statusCode === 200) {
        dispatch(setSalesData({
          tableData: analyticsRes.data.salesData.billDetails,
          graphData: {
            labels: analyticsRes.data.salesData.labels,
            data: analyticsRes.data.salesData.data
          },
          cashData: analyticsRes.data.paymentTypeWiseAmount,
          advance: analyticsRes.data.totalAdvanceAmount,
          expenses: analyticsRes.data.totalExpense
        }));
        dispatch(calculateTotals());
      }

      if (packageRes.statusCode === 200) {
        dispatch(setPackageMembershipData({
          packagedata: packageRes.data?.packageBills,
          memberdata: packageRes.data?.membershipBills
        }));
      }

      if (staffRes.statusCode === 200) {
        dispatch(setStaffGraphData({
          categories: staffRes.data.staffName.map(item => item || ''),
          series: staffRes.data.totalStaffSales
        }));
      }
    } finally {
      if (filterBy === 'Current Year') {
        dispatch(setLoadingCurrentYear(false));
      } else {
        dispatch(setLoading(false));
      }
    }
  };

  const handleApply = () => {
    dispatch(setFilterParams({ filterBy, from, to }));
    fetchSalesData();
    setApplyTrigger(prev => prev + 1);
  };

  const handleOnExport = () => {
    if (activeComponent === 'SalesReport') {
      if (!Array.isArray(tableData)) return;

      const flattenedData = tableData.map((item) => ({
        ...item,
        serviceName: item.services?.map(s => s.serviceName).join(', ') || '',
        serviceProviders: item.services?.map(s => s.serviceProvider?.name).join(', ') || '',
        productName: item.products?.map(p => p.productName).join(', ') || '',
        productProvider: item.products?.map(p => p.providerStaff?.name).join(', ') || ''
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(flattenedData);
      XLSX.utils.book_append_sheet(wb, ws, 'Bills');
      XLSX.writeFile(wb, 'Bills.xlsx');
    } else if (activeComponent === 'NetSales') {
      if (!summaryData?.dailySummary) return;

      const exportData = summaryData.dailySummary.map(item => {
        const [year, month, day] = item.date.split('-');
        return {
          'Date': `${day} ${month} ${year}`,
          'Total Sales': item.totalSales,
          'Total Expense': item.totalExpense,
          'Net Sales': item.netSales
        };
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, 'Net Sales Summary');
      XLSX.writeFile(wb, 'NetSalesSummary.xlsx');
    }
  };

  useEffect(() => {
    if (activeBranchId) {
      getData2(activeBranchId);
      if (activeComponent === 'SalesReport') {
        fetchSalesData();
      }
      setApplyTrigger(prev => prev + 1);
    }
  }, [activeBranchId]);

  return (
    <>
      <PageContainer title="Sales Analytics" description="This is Sales Analytics">
        <DashboardCard2 title="Sales Analytics">
          {loadingLocal ? (
            <ShowLoader />
          ) : (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '1rem' }}>

                <ToggleButtonGroup
                  value={activeComponent}
                  exclusive
                  onChange={handleToggleChange}
                  sx={{
                    borderRadius: '30px',
                    height: '40px',
                    backgroundColor: '#f0f0f0',
                    border: '1px solid #cfcece',
                    display: 'flex',
                    gap: '2px',
                    // width: '20%',
                    marginLeft: '0rem',
                  }}
                >
                  <ToggleButton
                    value="SalesReport"
                    sx={{
                      color: '#000',
                      backgroundColor: '#fafafa',
                      // flex: 1,
                      padding: '0 20px',
                      borderRadius: '30px',
                      border: 'none', // Remove border
                      '&.Mui-selected': {
                        backgroundColor: '#6174DD',
                        color: '#fff',
                        border: 'none', // Remove border for selected button
                        '&:hover': {
                          backgroundColor: '#6174DD',
                        },
                      },
                      '&:not(.Mui-selected)': {
                        '&:hover': {
                          backgroundColor: '#d0d0d0',
                        },
                      },
                    }}
                  >
                    Sales Report
                  </ToggleButton>
                  <ToggleButton
                    value="GstBillsPage"
                    sx={{
                      color: '#000',
                      backgroundColor: '#fafafa',
                      // flex: 1,
                      padding: '0 20px',
                      borderRadius: '30px',
                      border: 'none', // Remove border
                      '&.Mui-selected': {
                        backgroundColor: '#6174DD',
                        color: '#fff',
                        border: 'none', // Remove border for selected button
                        '&:hover': {
                          backgroundColor: '#6174DD',
                        },
                      },
                      '&:not(.Mui-selected)': {
                        '&:hover': {
                          backgroundColor: '#d0d0d0',
                        },
                      },
                    }}
                  >
                    GST Bills
                  </ToggleButton>
                  <ToggleButton
                    value="NetSales"
                    sx={{
                      color: '#000',
                      backgroundColor: '#fafafa',
                      padding: '0 20px',
                      borderRadius: '30px',
                      border: 'none',
                      '&.Mui-selected': {
                        backgroundColor: '#6174DD',
                        color: '#fff',
                        border: 'none',
                        '&:hover': {
                          backgroundColor: '#6174DD',
                        },
                      },
                      '&:not(.Mui-selected)': {
                        '&:hover': {
                          backgroundColor: '#d0d0d0',
                        },
                      },
                    }}
                  >
                    Net Sales
                  </ToggleButton>
                </ToggleButtonGroup>

                {(activeComponent === 'SalesReport' || activeComponent === 'NetSales') && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', flex: 1, justifyContent: 'flex-end' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Filter By:</div>
                      <FormControl>
                        <Select
                          name="Filter Options"
                          onChange={(e) => dispatch(setFilterParams({ filterBy: e.target.value, from, to }))}
                          value={filterBy}
                          sx={{ height: '36px', fontSize: '0.875rem', width: '180px' }}
                        >
                          {filterOptions?.map((sourceOption) => (
                            <MenuItem key={sourceOption.value} value={sourceOption.value}>
                              {sourceOption.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>

                    {filterBy === 'customDate' && (
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <div style={{ fontWeight: 'bold' }}>From:</div>
                          <input
                            type="date"
                            className="form-control"
                            style={{ padding: '8px', height: '36px', border: '1px solid #ccc', borderRadius: '4px' }}
                            name="from"
                            onChange={(e) => dispatch(setFilterParams({ filterBy, from: e.target.value, to }))}
                            value={from}
                          />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <div style={{ fontWeight: 'bold' }}>To:</div>
                          <input
                            type="date"
                            max={new Date().toISOString().split('T')[0]}
                            className="form-control"
                            style={{ padding: '8px', height: '36px', border: '1px solid #ccc', borderRadius: '4px' }}
                            name="to"
                            onChange={(e) => dispatch(setFilterParams({ filterBy, from, to: e.target.value }))}
                            value={to}
                          />
                        </div>
                      </div>
                    )}

                    <Button
                      disabled={!!(filterBy === 'customDate' && (from === '' || to === ''))}
                      variant="contained"
                      color="primary"
                      sx={{
                        background: '#6174DD',
                        padding: '6px 16px',
                        fontSize: '0.875rem',
                        height: '36px'
                      }}
                      onClick={handleApply}
                    >
                      Apply
                    </Button>
                    {!branchData?.hideDownloadButton && (
                      (activeComponent === 'SalesReport' && tableData.length !== 0) ||
                      (activeComponent === 'NetSales' && summaryData?.dailySummary?.length > 0)
                    ) && (
                      <button title="Download Excel File" className="view-btn" onClick={handleOnExport} style={{ height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '0 15px' }}>
                        <FaDownload style={{ fontSize: '1rem' }} />
                        Download Excel
                      </button>
                    )}

                  </div>
                )}
              </div>

              <div className="content">
                {activeComponent === 'SalesReport' && <SalesReport />}
                {activeComponent === 'GstBillsPage' && <GstBillsPage />}
                {activeComponent === 'NetSales' && <FetchSalesExpenseSummary applyTrigger={applyTrigger} setSummaryDataExport={setSummaryData} />}
              </div>
            </div>
          )}
        </DashboardCard2>
      </PageContainer>
    </>
  );
}

export default Analytics;